import { dailyDateString, dailyPuzzle } from './generator';
import type { Cell, Constraint, Difficulty, Tile } from './types';

// ─────────────────────────────────────────────────────────────────────
// Persistence
// ─────────────────────────────────────────────────────────────────────
// `streak` is the user-visible "days in a row played" counter, derived
// from `playedDates`. We keep it stored so the header can render before
// playedDates is rehydrated, but it always reflects whatever the dates
// imply on the current calendar day.
export interface StreakInfo {
  streak: number;
  /// Sorted YYYY-MM-DD strings (America/New_York) for every day the
  /// user has engaged with a daily puzzle. New entries are added on
  /// first board action AND on solve so anonymous players who only ever
  /// solve still get credited.
  playedDates: string[];
}

export function loadStreak(): StreakInfo {
  try {
    const raw = localStorage.getItem('hm.streak');
    if (!raw) return { streak: 0, playedDates: [] };
    const parsed = JSON.parse(raw) as Partial<StreakInfo> & { lastWinDay?: string | null };
    const playedDates = Array.isArray(parsed.playedDates) ? parsed.playedDates.slice() : [];
    // Legacy rows used `{streak, lastWinDay}`. The old `streak` counter
    // overcounted same-day solves so we can't trust it as a date set,
    // but we can seed playedDates with the lastWinDay so the user
    // doesn't lose their "I played yesterday" signal across the rollout.
    if (playedDates.length === 0 && parsed.lastWinDay) playedDates.push(parsed.lastWinDay);
    const sorted = [...new Set(playedDates)].sort();
    return { streak: streakFromPlayedDates(sorted), playedDates: sorted };
  } catch { return { streak: 0, playedDates: [] }; }
}
export function saveStreak(s: StreakInfo): void {
  try { localStorage.setItem('hm.streak', JSON.stringify(s)); } catch {/* ignore */}
}

// Add a play-day to the set and recompute the streak. Idempotent — a
// second call on the same date returns the same value unchanged.
export function markPlayed(prev: StreakInfo, today: string = dailyDateString()): StreakInfo {
  if (prev.playedDates.includes(today)) {
    // Streak can still shift if `today` has rolled over since the last
    // save (e.g. browser left open past midnight ET), so recompute.
    const streak = streakFromPlayedDates(prev.playedDates, today);
    return streak === prev.streak ? prev : { ...prev, streak };
  }
  const playedDates = [...prev.playedDates, today].sort();
  return { streak: streakFromPlayedDates(playedDates, today), playedDates };
}

function shiftDate(s: string, delta: number): string {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}

// Count consecutive days ending at today (or yesterday if today hasn't
// been played yet — today is "still in progress, not a gap"). Mirrors
// computeStats() on the server.
function streakFromPlayedDates(dates: string[], today: string = dailyDateString()): number {
  const set = new Set(dates);
  let streak = 0;
  let cursor = set.has(today) ? today : shiftDate(today, -1);
  while (set.has(cursor)) {
    streak++;
    cursor = shiftDate(cursor, -1);
  }
  return streak;
}
export function loadTheme(): 'light' | 'dark' {
  try {
    const t = localStorage.getItem('hm.theme');
    if (t === 'light' || t === 'dark') return t;
  } catch {/* ignore */}
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}
export function saveTheme(t: 'light' | 'dark'): void {
  try { localStorage.setItem('hm.theme', t); } catch {/* ignore */}
}

// Persist a finished puzzle so the solved board stays after reload.
// Keyed on (difficulty, puzzle-date). In-progress games are not persisted.
interface SavedSolved {
  cells: Cell[];
  bag: Tile[];
  constraints: Constraint[];
  name: string;
  elapsedAtSolve: number;
}
function solvedKey(difficulty: Difficulty, date: Date = new Date()): string {
  return `hm.solved.${difficulty}.${dailyDateString(date)}`;
}
export function loadSolvedGame(difficulty: Difficulty, date: Date = new Date()): SavedSolved | null {
  try {
    const raw = localStorage.getItem(solvedKey(difficulty, date));
    if (!raw) return null;
    return JSON.parse(raw) as SavedSolved;
  } catch { return null; }
}
export function saveSolvedGame(difficulty: Difficulty, payload: SavedSolved, date: Date = new Date()): void {
  try { localStorage.setItem(solvedKey(difficulty, date), JSON.stringify(payload)); } catch {/* ignore */}
}

// ─────────────────────────────────────────────────────────────────────
// Game state — reducer
// ─────────────────────────────────────────────────────────────────────
export interface GameState {
  difficulty: Difficulty;
  /// YYYY-MM-DD in America/New_York. Today by default; past dates land
  /// here when the user opens an archive puzzle (premium-only).
  puzzleDate: string;
  name: string;
  cells: Cell[];
  bag: Tile[];
  constraints: Constraint[];
  selectedTileId: string | null;
  history: HistoryEntry[];
  startedAt: number;
  solved: boolean;
  elapsedAtSolve: number;
  recentlyPlaced: number | null;
}

export type HistoryEntry =
  | { kind: 'pickup'; cellIdx: number; tileId: string; prevCell: Cell }
  | { kind: 'place';  cellIdx: number; tileId: string; prevCell: Cell }
  | { kind: 'move';   fromIdx: number; toIdx: number; tileId: string; prevFromCell: Cell; prevToCell: Cell }
  | { kind: 'autofill'; prevCells: Cell[]; prevBag: Tile[] };

export type Action =
  | { type: 'NEW_PUZZLE'; difficulty: Difficulty; date?: Date }
  | { type: 'SELECT_TILE'; tileId: string }
  | { type: 'PLACE_OR_PICKUP'; cellIdx: number }
  | { type: 'DRAG_FROM_BAG'; cellIdx: number; tileId: string }
  | { type: 'MOVE_CELL'; fromIdx: number; toIdx: number }
  | { type: 'AUTOFILL'; cells: Cell[]; bag: Tile[] }
  | { type: 'CLEAR_PLACE_HIGHLIGHT' }
  | { type: 'UNDO' }
  | { type: 'RESET' }
  | { type: 'MARK_SOLVED'; elapsed: number }
  | { type: 'LOAD_PROGRESS'; cells: Cell[]; bag: Tile[]; solved: boolean; elapsedMs: number | null }
  | { type: 'RESTORE'; snapshot: GameState };

// Build an initial GameState for the given difficulty, using the
// procedural generator seeded by the date in America/New_York.
// `date` defaults to "now" — passing a past date is how the future
// premium archive will deliver yesterday's puzzles.
export function newPuzzleState(difficulty: Difficulty, date: Date = new Date()): GameState {
  const puzzleDate = dailyDateString(date);
  // If today's puzzle for this difficulty has already been solved, restore
  // that finished board so the user sees it as-completed after reload.
  const saved = loadSolvedGame(difficulty, date);
  if (saved) {
    return {
      difficulty,
      puzzleDate,
      name: saved.name,
      cells: saved.cells.map(c => ({ ...c })),
      bag: saved.bag.map(t => ({ ...t })),
      constraints: saved.constraints,
      selectedTileId: null,
      history: [],
      startedAt: Date.now() - saved.elapsedAtSolve,
      solved: true,
      elapsedAtSolve: saved.elapsedAtSolve,
      recentlyPlaced: null,
    };
  }
  const puzzle = dailyPuzzle(difficulty, date);
  return {
    difficulty,
    puzzleDate,
    name: puzzle.name,
    cells: puzzle.cells.map(c => ({ ...c })),
    bag: puzzle.bag.map(t => ({ ...t, placed: false })),
    constraints: puzzle.constraints,
    selectedTileId: null,
    history: [],
    startedAt: Date.now(),
    solved: false,
    elapsedAtSolve: 0,
    recentlyPlaced: null,
  };
}

// Parse a YYYY-MM-DD string back to a JS Date suitable for passing to
// the generator. We pin to noon UTC so DST and tz boundaries don't
// nudge the calendar day when Intl picks a different timezone.
export function dateFromPuzzleDate(s: string): Date {
  return new Date(`${s}T12:00:00Z`);
}

export function gameReducer(state: GameState, action: Action): GameState {
  switch (action.type) {
    case 'NEW_PUZZLE': {
      return newPuzzleState(action.difficulty, action.date);
    }
    case 'SELECT_TILE': {
      if (state.solved) return state;
      const tile = state.bag.find(t => t.id === action.tileId);
      if (!tile || tile.placed) return state;
      return { ...state, selectedTileId: state.selectedTileId === action.tileId ? null : action.tileId };
    }
    case 'PLACE_OR_PICKUP': {
      if (state.solved) return state;
      const i = action.cellIdx;
      const cell = state.cells[i];

      // Pickup: clicking a placed (non-locked) tile returns it to the bag
      if (cell.kind === 'placed') {
        const tileId = cell.placedTileId!;
        const newCells = state.cells.slice();
        newCells[i] = {
          kind: 'empty',
          ...(cell.modifier != null ? { modifier: cell.modifier } : {}),
          ...(cell._restriction != null ? { _restriction: cell._restriction } : {}),
        };
        const newBag = state.bag.map(t =>
          t.id === tileId ? { ...t, placed: false } : t
        );
        return {
          ...state,
          cells: newCells,
          bag: newBag,
          history: state.history.concat([{ kind: 'place', cellIdx: i, tileId, prevCell: cell }]),
          selectedTileId: tileId,
          recentlyPlaced: null,
        };
      }

      if (cell.kind !== 'empty') return state;
      if (!state.selectedTileId) return state;
      const tile = state.bag.find(t => t.id === state.selectedTileId);
      if (!tile || tile.placed) return state;

      const newCells = state.cells.slice();
      newCells[i] = {
        kind: 'placed',
        value: tile.value,
        placedTileId: tile.id,
        ...(cell.modifier != null ? { modifier: cell.modifier } : {}),
        ...(cell._restriction != null ? { _restriction: cell._restriction } : {}),
      };
      const newBag = state.bag.map(t =>
        t.id === tile.id ? { ...t, placed: true } : t
      );

      return {
        ...state,
        cells: newCells,
        bag: newBag,
        selectedTileId: null,
        history: state.history.concat([{ kind: 'pickup', cellIdx: i, tileId: tile.id, prevCell: cell }]),
        recentlyPlaced: i,
      };
    }
    case 'DRAG_FROM_BAG': {
      if (state.solved) return state;
      const cellIdx = action.cellIdx;
      const tileId = action.tileId;
      const cell = state.cells[cellIdx];
      if (cell.kind !== 'empty') return state;
      const tile = state.bag.find(t => t.id === tileId);
      if (!tile || tile.placed) return state;

      const newCells = state.cells.slice();
      newCells[cellIdx] = {
        kind: 'placed',
        value: tile.value,
        placedTileId: tile.id,
        ...(cell.modifier != null ? { modifier: cell.modifier } : {}),
        ...(cell._restriction != null ? { _restriction: cell._restriction } : {}),
      };
      const newBag = state.bag.map(t =>
        t.id === tile.id ? { ...t, placed: true } : t
      );
      return {
        ...state,
        cells: newCells,
        bag: newBag,
        selectedTileId: null,
        history: state.history.concat([{ kind: 'pickup', cellIdx, tileId: tile.id, prevCell: cell }]),
        recentlyPlaced: cellIdx,
      };
    }
    case 'MOVE_CELL': {
      if (state.solved) return state;
      const fromIdx = action.fromIdx;
      const toIdx = action.toIdx;
      if (fromIdx === toIdx) return state;
      const fromCell = state.cells[fromIdx];
      const toCell = state.cells[toIdx];
      if (fromCell.kind !== 'placed') return state;
      // Locked cells are fixed — never overwrite or swap with them.
      if (toCell.kind === 'locked') return state;
      const tile = state.bag.find(t => t.id === fromCell.placedTileId);
      if (!tile) return state;

      const newCells = state.cells.slice();
      if (toCell.kind === 'empty') {
        // Move into empty cell — clear source, place tile at target.
        newCells[fromIdx] = {
          kind: 'empty',
          ...(fromCell.modifier != null ? { modifier: fromCell.modifier } : {}),
          ...(fromCell._restriction != null ? { _restriction: fromCell._restriction } : {}),
        };
        newCells[toIdx] = {
          kind: 'placed',
          value: tile.value,
          placedTileId: tile.id,
          ...(toCell.modifier != null ? { modifier: toCell.modifier } : {}),
          ...(toCell._restriction != null ? { _restriction: toCell._restriction } : {}),
        };
      } else if (toCell.kind === 'placed') {
        // Swap two placed tiles — each cell keeps its own modifier/restriction
        // overlay; only the tile values/ids switch.
        const otherTile = state.bag.find(t => t.id === toCell.placedTileId);
        if (!otherTile) return state;
        newCells[fromIdx] = {
          kind: 'placed',
          value: otherTile.value,
          placedTileId: otherTile.id,
          ...(fromCell.modifier != null ? { modifier: fromCell.modifier } : {}),
          ...(fromCell._restriction != null ? { _restriction: fromCell._restriction } : {}),
        };
        newCells[toIdx] = {
          kind: 'placed',
          value: tile.value,
          placedTileId: tile.id,
          ...(toCell.modifier != null ? { modifier: toCell.modifier } : {}),
          ...(toCell._restriction != null ? { _restriction: toCell._restriction } : {}),
        };
      } else {
        return state;
      }

      return {
        ...state,
        cells: newCells,
        selectedTileId: null,
        history: state.history.concat([{
          kind: 'move',
          fromIdx, toIdx,
          tileId: tile.id,
          prevFromCell: fromCell,
          prevToCell: toCell,
        }]),
        recentlyPlaced: toIdx,
      };
    }
    case 'CLEAR_PLACE_HIGHLIGHT':
      return { ...state, recentlyPlaced: null };

    case 'AUTOFILL': {
      if (state.solved) return state;
      return {
        ...state,
        cells: action.cells.map(c => ({ ...c })),
        bag: action.bag.map(t => ({ ...t })),
        selectedTileId: null,
        history: state.history.concat([{
          kind: 'autofill',
          prevCells: state.cells.map(c => ({ ...c })),
          prevBag: state.bag.map(t => ({ ...t })),
        }]),
        recentlyPlaced: null,
      };
    }

    case 'UNDO': {
      if (state.history.length === 0) return state;
      const last = state.history[state.history.length - 1];
      const newHistory = state.history.slice(0, -1);
      let newCells = state.cells.slice();
      let newBag = state.bag.slice();

      if (last.kind === 'pickup') {
        newCells[last.cellIdx] = last.prevCell;
        const idx = newBag.findIndex(t => t.id === last.tileId);
        if (idx >= 0) newBag[idx] = { ...newBag[idx], placed: false };
      } else if (last.kind === 'place') {
        newCells[last.cellIdx] = last.prevCell;
        const idx = newBag.findIndex(t => t.id === last.tileId);
        if (idx >= 0) newBag[idx] = { ...newBag[idx], placed: true };
      } else if (last.kind === 'move') {
        newCells[last.fromIdx] = last.prevFromCell;
        newCells[last.toIdx] = last.prevToCell;
      } else if (last.kind === 'autofill') {
        newCells = last.prevCells.map(c => ({ ...c }));
        newBag = last.prevBag.map(t => ({ ...t }));
      }
      return {
        ...state,
        cells: newCells,
        bag: newBag,
        history: newHistory,
        recentlyPlaced: null,
      };
    }
    case 'RESET': {
      // Preserve the active puzzle date — RESET on an archive puzzle
      // should re-roll the same puzzle, not jump back to today.
      return newPuzzleState(state.difficulty, dateFromPuzzleDate(state.puzzleDate));
    }
    case 'MARK_SOLVED': {
      return { ...state, solved: true, elapsedAtSolve: action.elapsed };
    }
    case 'RESTORE': {
      // Wholesale replace the state with a saved snapshot. Used by the
      // tutorial's Back button to rewind to the state at the moment the
      // previous step was entered, so predicate-driven steps don't
      // immediately re-fire and bounce the user forward.
      return action.snapshot;
    }
    case 'LOAD_PROGRESS': {
      // Replace cells + bag with the saved snapshot. Constraints / name
      // come from the freshly-generated puzzle (which is deterministic
      // for a given date+difficulty), so the puzzle "shape" is intact;
      // we just swap in player progress on top. Wipes history because
      // there's nothing meaningful to undo onto a remote-loaded board.
      return {
        ...state,
        cells: action.cells,
        bag: action.bag,
        history: [],
        selectedTileId: null,
        recentlyPlaced: null,
        solved: action.solved,
        elapsedAtSolve: action.solved ? (action.elapsedMs ?? state.elapsedAtSolve) : 0,
      };
    }
    default:
      return state;
  }
}
